import { useState, useCallback, useMemo } from "react";
import { SECTIONS, QUESTIONS, LIKERT_OPTIONS } from "../data/questions";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";

function SectionMicroBar({ questions, answers }) {
  return (
    <div className="mt-4 flex gap-1.5">
      {questions.map((q) => (
        <div
          key={`micro-${q.id}`}
          className={`flex-1 h-0.5 rounded-full transition-all duration-500 ${
            answers[q.id] !== undefined ? "bg-[#F3B21A]" : "bg-[#E8C8BE]"
          }`}
        />
      ))}
    </div>
  );
}

function QuestionCard({ question, answer, justAnswered, onAnswer }) {
  const isAnswered = answer !== undefined;
  return (
    <div
      data-testid={`question-card-${question.id}`}
      className={`bg-white border p-4 sm:p-6 lg:p-8 transition-all duration-300 ${
        isAnswered ? "border-[#F3B21A] shadow-sm" : "border-[#E8C8BE]"
      } ${justAnswered ? "scale-[1.01]" : ""}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-body font-medium flex-shrink-0 transition-all duration-300 ${
            isAnswered ? "bg-[#F3B21A] text-[#054452]" : "bg-[#E8C8BE] text-[#0C6474]"
          }`}
        >
          {isAnswered ? <Check size={12} /> : question.id}
        </span>
        <p className="font-body text-[#054452] text-sm sm:text-base font-normal leading-relaxed">
          {question.text}
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5 sm:gap-2 ml-0 sm:ml-9">
        {LIKERT_OPTIONS.map((option) => (
          <button
            key={`q${question.id}-opt${option.value}`}
            onClick={() => onAnswer(question.id, option.value)}
            data-testid={`question-${question.id}-option-${option.value}`}
            className={`likert-btn flex-1 min-w-[60px] sm:min-w-[90px] py-2 sm:py-2.5 px-1.5 sm:px-2 text-[10px] sm:text-xs font-body font-normal border transition-all duration-200 ${
              answer === option.value
                ? "bg-[#054452] text-white border-[#054452] shadow-sm"
                : "bg-white text-[#0C6474] border-[#E8C8BE] hover:border-[#F3B21A] hover:text-[#054452] active:scale-[0.96]"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NavigationBar({ currentSection, totalSections, isSectionComplete, onBack, onNext }) {
  return (
    <div className="flex items-center justify-between mt-8 sm:mt-10">
      {currentSection > 0 ? (
        <button
          onClick={onBack}
          data-testid="back-btn"
          className="flex items-center gap-2 border border-[#E8C8BE] text-[#054452] px-4 sm:px-6 py-3 font-body text-xs sm:text-sm hover:border-[#F3B21A] transition-colors active:scale-[0.98]"
        >
          <ArrowLeft size={14} />
          Back
        </button>
      ) : (
        <div />
      )}

      <button
        onClick={onNext}
        disabled={!isSectionComplete}
        data-testid="continue-btn"
        className={`flex items-center gap-2 px-6 sm:px-8 py-3 font-body text-xs sm:text-sm transition-all duration-300 active:scale-[0.98] ${
          isSectionComplete
            ? "bg-[#054452] text-white hover:bg-[#035162]"
            : "bg-[#E8C8BE] text-[#0C6474] cursor-not-allowed"
        }`}
      >
        {currentSection === totalSections - 1 ? (
          <>
            <Sparkles size={14} />
            See My Results
          </>
        ) : (
          <>
            Continue
            <ArrowRight size={14} />
          </>
        )}
      </button>
    </div>
  );
}

export default function Assessment({ onComplete }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [justAnswered, setJustAnswered] = useState(null);
  const [sectionTransition, setSectionTransition] = useState(false);

  const section = SECTIONS[currentSection];
  const totalSections = SECTIONS.length;
  const totalQuestions = QUESTIONS.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  const sectionQuestions = useMemo(
    () => QUESTIONS.filter((q) => q.section === section.id),
    [section.id]
  );

  const isSectionComplete = useMemo(
    () => sectionQuestions.every((q) => answers[q.id] !== undefined),
    [sectionQuestions, answers]
  );

  const handleAnswer = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setJustAnswered(questionId);
    setTimeout(() => setJustAnswered(null), 600);
  }, []);

  const handleNext = useCallback(() => {
    if (currentSection < totalSections - 1) {
      setSectionTransition(true);
      setTimeout(() => {
        setCurrentSection((prev) => prev + 1);
        setSectionTransition(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    } else {
      const formattedAnswers = {};
      Object.entries(answers).forEach(([key, val]) => {
        formattedAnswers[String(key)] = val;
      });
      onComplete(formattedAnswers);
    }
  }, [currentSection, totalSections, answers, onComplete]);

  const handleBack = useCallback(() => {
    if (currentSection > 0) {
      setSectionTransition(true);
      setTimeout(() => {
        setCurrentSection((prev) => prev - 1);
        setSectionTransition(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    }
  }, [currentSection]);

  const sectionAnsweredCount = useMemo(
    () => sectionQuestions.filter((q) => answers[q.id] !== undefined).length,
    [sectionQuestions, answers]
  );

  return (
    <div data-testid="assessment-screen" className="min-h-screen bg-[#FDF6F3]">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-[#FDF6F3] border-b border-[#E8C8BE]">
        <div className="w-full h-1 bg-[#E8C8BE]">
          <div
            className="h-full bg-[#F3B21A] progress-fill"
            style={{ width: `${progress}%` }}
            data-testid="progress-bar"
          />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-2">
          <span className="font-body text-[10px] sm:text-xs text-[#0C6474]">
            {answeredCount} of {totalQuestions} answered
          </span>
          <div className="flex items-center gap-1.5">
            {SECTIONS.map((s, idx) => (
              <div
                key={s.id}
                className={`w-6 sm:w-8 h-1 rounded-full transition-colors duration-300 ${
                  idx < currentSection
                    ? "bg-[#F3B21A]"
                    : idx === currentSection
                    ? "bg-[#E66E6D]"
                    : "bg-[#E8C8BE]"
                }`}
              />
            ))}
          </div>
          <span className="font-body text-[10px] sm:text-xs text-[#0C6474]">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Section Header */}
      <div
        className={`px-4 sm:px-6 pt-6 sm:pt-10 pb-4 sm:pb-6 max-w-[680px] mx-auto transition-opacity duration-300 ${
          sectionTransition ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="font-body text-xs tracking-[0.25em] uppercase text-[#F3B21A]"
            data-testid="section-label"
          >
            Section {section.id} of {totalSections}
          </span>
          {sectionAnsweredCount === sectionQuestions.length && (
            <span className="flex items-center gap-1 text-[#F3B21A] text-xs font-body">
              <Check size={12} /> Complete
            </span>
          )}
        </div>
        <h2
          className="font-display text-2xl sm:text-3xl lg:text-4xl font-light text-[#054452] mb-2 sm:mb-3"
          data-testid="section-title"
        >
          {section.title}
        </h2>
        <p
          className="font-body text-xs sm:text-sm text-[#0C6474] font-light italic max-w-[520px]"
          data-testid="section-subtitle"
        >
          {section.subtitle}
        </p>
        <SectionMicroBar questions={sectionQuestions} answers={answers} />
      </div>

      {/* Questions */}
      <div
        className={`px-4 sm:px-6 pb-10 max-w-[680px] mx-auto transition-opacity duration-300 ${
          sectionTransition ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="flex flex-col gap-3 sm:gap-4">
          {sectionQuestions.map((question) => (
            <QuestionCard
              key={`question-${question.id}`}
              question={question}
              answer={answers[question.id]}
              justAnswered={justAnswered === question.id}
              onAnswer={handleAnswer}
            />
          ))}
        </div>

        <NavigationBar
          currentSection={currentSection}
          totalSections={totalSections}
          isSectionComplete={isSectionComplete}
          onBack={handleBack}
          onNext={handleNext}
        />
      </div>
    </div>
  );
}
